import { useState, useEffect, useCallback } from 'react'
import { MainLayout } from '../../components/layout/MainLayout'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { Plus, Search, Edit2, Trash2, ChevronLeft, ChevronRight } from 'lucide-react'
import { produtoService } from '../../services/produtoService'
import { industriaService } from '../../services/industriaService'
import type { Produto, Industria } from '../../types'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from '../../utils/toast'
import { TableSkeleton } from '../../components/ui/Skeleton'

const produtoSchema = z.object({
  codigo: z.string().min(1, 'Código obrigatório'),
  nome: z.string().min(3, 'Nome deve ter no mínimo 3 caracteres'),
  industriaId: z.string().min(1, 'Selecione uma indústria'),
  ativo: z.boolean(),
})

type ProdutoFormData = z.infer<typeof produtoSchema>

export function ProdutosPage() {
  const [produtos, setProdutos] = useState<Produto[]>([])
  const [industrias, setIndustrias] = useState<Industria[]>([])
  const [loading, setLoading] = useState(true)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingProduto, setEditingProduto] = useState<Produto | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [industriaFilter, setIndustriaFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | 'ativo' | 'inativo'>('all')
  
  // Paginação controlada pela API
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProdutoFormData>({
    resolver: zodResolver(produtoSchema),
    defaultValues: { ativo: true },
  })

  const loadIndustrias = useCallback(async () => {
    try {
      const result = await industriaService.getAll({
        limit: 1000, // Carregar todas para o filtro
        status: 'ativo'
      })
      setIndustrias(result.data)
    } catch (error) {
      console.error('Erro ao carregar indústrias:', error)
    }
  }, [])

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const result = await produtoService.getAll({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        industriaId: industriaFilter === 'all' ? undefined : industriaFilter,
        status: statusFilter === 'all' ? undefined : statusFilter,
        sort: 'nome:asc'
      })

      setProdutos(result.data)
      setTotalPages(result.pagination.totalPages)
      setTotalItems(result.pagination.total)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao carregar produtos'
      toast.error(message)
      console.error('Erro ao carregar produtos:', error)
    } finally {
      setLoading(false)
      setIsInitialLoad(false)
    }
  }, [currentPage, itemsPerPage, searchTerm, industriaFilter, statusFilter])

  useEffect(() => {
    if (isInitialLoad) {
      loadData()
    }
  }, [isInitialLoad, loadData])

  // Recarrega quando filtros/paginação mudam (após load inicial)
  useEffect(() => {
    if (!isInitialLoad) {
      loadData()
    }
  }, [currentPage, industriaFilter, statusFilter])

  // Debounce para busca
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1)
      } else {
        loadData()
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [searchTerm, currentPage, loadData])

  // Carregar indústrias uma vez
  useEffect(() => {
    loadIndustrias()
  }, [loadIndustrias])

  const openModal = (produto?: Produto) => {
    if (produto) {
      setEditingProduto(produto)
      reset({
        codigo: produto.codigo,
        nome: produto.nome,
        industriaId: produto.industriaId,
        ativo: produto.ativo,
      })
    } else {
      setEditingProduto(null)
      reset({ codigo: '', nome: '', industriaId: '', ativo: true })
    }
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingProduto(null)
    reset()
  }

  const onSubmit = async (data: ProdutoFormData) => {
    try {
      const payload = {
        codigo: data.codigo.toUpperCase(),
        nome: data.nome,
        industriaId: data.industriaId,
        ativo: data.ativo,
      }

      if (editingProduto) {
        await produtoService.update(editingProduto.id, payload)
        toast.success('Produto atualizado com sucesso!')
      } else {
        await produtoService.create(payload)
        toast.success('Produto criado com sucesso!')
      }

      await loadData()
      closeModal()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao salvar produto'
      toast.error(message)
      console.error('Erro ao salvar produto:', error)
    }
  }

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir este produto?')) {
      try {
        await produtoService.delete(id)
        await loadData()
        toast.success('Produto excluído com sucesso!')
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erro ao excluir produto'
        toast.error(message)
        console.error('Erro ao excluir produto:', error)
      }
    }
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Produtos</h1>
          <Button onClick={() => openModal()}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Produto
          </Button>
        </div>

        <Card>
          {/* Filters */}
          <div className="mb-6 flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Buscar por nome ou código..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <select
                value={industriaFilter}
                onChange={(e) => setIndustriaFilter(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="all">Todas as indústrias</option>
                {industrias.map((i) => (
                  <option key={i.id} value={i.id}>{i.nomeFantasia}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setStatusFilter('all')}
                className={`rounded-lg px-4 py-2 text-sm font-medium ${
                  statusFilter === 'all'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                Todos
              </button>
              <button
                onClick={() => setStatusFilter('ativo')}
                className={`rounded-lg px-4 py-2 text-sm font-medium ${
                  statusFilter === 'ativo'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                Ativos
              </button>
              <button
                onClick={() => setStatusFilter('inativo')}
                className={`rounded-lg px-4 py-2 text-sm font-medium ${
                  statusFilter === 'inativo'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                Inativos
              </button>
            </div>
          </div>

          {loading && isInitialLoad ? (
            <TableSkeleton />
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto animate-fadeIn" style={{ minHeight: '200px' }}>
                <table className="w-full">
                  <thead className="border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Código</th>
                      <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Nome</th>
                      <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Indústria</th>
                      <th className="pb-3 text-right text-sm font-semibold text-gray-900 dark:text-white">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {produtos.map((produto) => (
                      <tr key={produto.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="py-4 text-sm font-medium text-gray-900 dark:text-white">{produto.codigo}</td>
                        <td className="py-4 text-sm text-gray-900 dark:text-white">{produto.nome}</td>
                        <td className="py-4 text-sm text-gray-500 dark:text-gray-400">{produto.industria?.nomeFantasia}</td>
                        <td className="py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openModal(produto)}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(produto.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {produtos.length === 0 && (
                  <div className="py-12 text-center">
                    <p className="text-gray-500 dark:text-gray-400">Nenhum produto encontrado</p>
                  </div>
                )}
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-4 animate-fadeIn">
                {produtos.length === 0 ? (
                  <div className="py-12 text-center">
                    <p className="text-gray-500 dark:text-gray-400">Nenhum produto encontrado</p>
                  </div>
                ) : (
                  produtos.map((produto) => (
                    <div
                      key={produto.id}
                      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
                    >
                      <div className="mb-3">
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {produto.nome}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Código: {produto.codigo}
                        </p>
                      </div>
                      
                      <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Indústria:</span>
                        <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                          {produto.industria?.nomeFantasia}
                        </p>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => openModal(produto)}
                        >
                          <Edit2 className="h-4 w-4 mr-2" />
                          Editar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(produto.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {/* Paginação */}
          {!loading && totalItems > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Mostrar
                </span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value))
                    setCurrentPage(1)
                  }}
                  className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-white"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  de {totalItems}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Página {currentPage} de {totalPages}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} title={editingProduto ? 'Editar Produto' : 'Novo Produto'}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input label="Código *" placeholder="AB001" error={errors.codigo?.message} {...register('codigo')} />
          <Input label="Nome *" placeholder="Nome do produto" error={errors.nome?.message} {...register('nome')} />
          
          <div>
            <label className="block text-sm font-medium mb-1.5 text-gray-700 dark:text-gray-300">Indústria *</label>
            <select
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              {...register('industriaId')}
            >
              <option value="">Selecione uma indústria</option>
              {industrias.map((i) => (
                <option key={i.id} value={i.id}>{i.nomeFantasia}</option>
              ))}
            </select>
            {errors.industriaId && <p className="text-sm text-red-500 mt-1">{errors.industriaId.message}</p>}
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" id="ativo" className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" {...register('ativo')} />
            <label htmlFor="ativo" className="text-sm font-medium text-gray-700 dark:text-gray-300">Produto ativo</label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="ghost" onClick={closeModal}>Cancelar</Button>
            <Button type="submit">{editingProduto ? 'Atualizar' : 'Criar'}</Button>
          </div>
        </form>
      </Modal>
    </MainLayout>
  )
}
